import React from 'react';
import _ from 'lodash';
import ActivateAbility from './ActivateAbility';
import ActivateDimension from './ActivateDimension';
import DeactivateAbility from './DeactivateAbility';
import DeactivateDimension from './DeactivateDimension';
import DimensionDisplayName from './DimensionDisplayName';
import AbilityDisplayName from './AbilityDisplayName';
import DeactivateDistortion from './DeactivateDistortion';
import ActivateDistortion from './ActivateDistortion';
import DistortionDisplayName from './DistortionDisplayName';
import DeactivateInterests from './DeactivateInterests';
import ActivateInterests from './ActivateInterests';

const generateTreeNodes = (data, fetchData, intlMessages) => {
  const { rootAccount, descendantAccounts } = data;

  const {
    id: rootAccountId,
    assessmentSections: rootAssessmentSections,
    surveyCompetencies: rootCompetencies,
  } = rootAccount;

  return descendantAccounts
    .map((account, index) => {
      const accountAssessmentSections = account.assessmentSections;
      const accountCompetencies = account.surveyCompetencies;
      const { abilitiesInUse } = account;
      const { dimensionsInUse } = account;

      const unionBehaviour = _.unionBy(rootAssessmentSections.behaviorSection.dimensions,
        accountAssessmentSections.behaviorSection.dimensions, dim => dim.id);
      const totalBehaviour = unionBehaviour.length;

      const unionDistortion = _.unionBy(rootAssessmentSections.behaviorSection.distortionDimensions,
        accountAssessmentSections.behaviorSection.distortionDimensions, dist => dist.id);
      const totalDistortion = unionDistortion.length;

      const unionCognitive = _.unionBy(rootAssessmentSections.cognitiveSection.dimensions,
        accountAssessmentSections.cognitiveSection.dimensions, dim => dim.id);
      const totalCognitive = unionCognitive.length;

      const unionInterests = _.unionBy(rootAssessmentSections.interestsSection.dimensions,
        accountAssessmentSections.interestsSection.dimensions, dim => dim.id);
      const totalInterests = unionInterests.length;

      const totalDimensions = totalBehaviour + totalCognitive + totalInterests;
      const unionCompetence = _.unionBy(rootCompetencies, accountCompetencies, comp => comp.displayName);
      const totalAbilities = _.sum(unionCompetence.map(comp => comp.abilities.length));

      const availableBehaviour = accountAssessmentSections.behaviorSection.dimensions.length;
      const availableDistortion = accountAssessmentSections.behaviorSection.distortionDimensions.length;
      const availableCognitive = accountAssessmentSections.cognitiveSection.dimensions.length;
      const availableInterests = accountAssessmentSections.interestsSection.dimensions.length;

      const availableTotalDimensions = availableBehaviour + availableCognitive + availableInterests;
      const availableTotalAbilities = _.sum(accountCompetencies.map(comp => comp.abilities.length));

      return ({
        key: index,
        data: {
          name: account.displayName,
          status: `${availableTotalDimensions}/${totalDimensions} ${intlMessages.dimensionsActivated} | 
          ${availableTotalAbilities}/${totalAbilities} ${intlMessages.abilitiesActivated}`,
        },
        children: [
          {
            key: `${index}-0`,
            data: {
              name: accountAssessmentSections.behaviorSection.displayName,
              status: `${availableBehaviour}/${totalBehaviour} ${intlMessages.dimensionsActivatedForSection}`,
            },
            children: [{
              key: `${index}-0-0`,
              data: {
                name: `${intlMessages.dimensions}`,
                status: `${availableBehaviour}/${totalBehaviour} ${intlMessages.dimensionsActivated}`,
              },
              children:
              _.sortBy(unionBehaviour, dim => dim.displayName)
                .map((dimension, dimIndex) => {
                  const isActive = _.includes(
                    accountAssessmentSections.behaviorSection.dimensions
                      .map(dim => dim.id),
                    dimension.id,
                  );
                  const alreadyInUse = _.includes(
                    dimensionsInUse,
                    dimension.name,
                  );
                  return ({
                    key: `${index}-0-0-${dimIndex}`,
                    data: {
                      name: <DimensionDisplayName isActive={isActive} dimension={dimension} />,
                      status:
                      isActive
                        ? (
                          <DeactivateDimension
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            assessmentSection="behavior"
                            dimension={dimension.name}
                            alreadyInUse={alreadyInUse}
                            fetchData={fetchData}
                          />
                        )
                        : (
                          <ActivateDimension
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            assessmentSection="behavior"
                            dimension={dimension.name}
                            fetchData={fetchData}
                          />
                        ),
                    },
                  });
                }),
            },
            {
              key: `${index}-0-1`,
              data: {
                name: `${intlMessages.distortions}`,
                status: `${availableDistortion}/${totalDistortion} ${intlMessages.distortionsActivated}`,
              },
              children: _.sortBy(unionDistortion, dist => dist.displayName)
                .map((distortion, distIndex) => {
                  const isActive = _.includes(
                    accountAssessmentSections.behaviorSection.distortionDimensions.map(dist => dist.id),
                    distortion.id,
                  );
                  const alreadyInUse = true;
                  return ({
                    key: `${index}-0-1-${distIndex}`,
                    data: {
                      name: <DistortionDisplayName distortion={distortion} isActive={isActive} />,
                      status:
                      isActive
                        ? (
                          <DeactivateDistortion
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            distortion={distortion.name}
                            alreadyInUse={alreadyInUse}
                            fetchData={fetchData}
                          />
                        ) : (
                          <ActivateDistortion
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            distortion={distortion.name}
                            fetchData={fetchData}
                          />
                        ),
                    },
                  });
                }),
            },
            ],
          },
          {
            key: `${index}-1`,
            data: {
              name: accountAssessmentSections.cognitiveSection.displayName,
              status: `${availableCognitive}/${totalCognitive} ${intlMessages.dimensionsActivatedForSection}`,
            },
            children: _.sortBy(unionCognitive, dim => dim.displayName)
              .map((dimension, dimIndex) => {
                const isActive = _.includes(
                  accountAssessmentSections.cognitiveSection.dimensions
                    .map(dim => dim.id),
                  dimension.id,
                );
                const alreadyInUse = _.includes(
                  dimensionsInUse,
                  dimension.name,
                );
                return ({
                  key: `${index}-1-${dimIndex}`,
                  data: {
                    name: <DimensionDisplayName isActive={isActive} dimension={dimension} />,
                    status:
                      isActive
                        ? (
                          <DeactivateDimension
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            assessmentSection="cognitive"
                            dimension={dimension.name}
                            alreadyInUse={alreadyInUse}
                            fetchData={fetchData}
                          />
                        )
                        : (
                          <ActivateDimension
                            sourceAccountId={+rootAccountId}
                            targetAccountId={+account.id}
                            assessmentSection="cognitive"
                            dimension={dimension.name}
                            fetchData={fetchData}
                          />
                        ),
                  },
                });
              }),
          },
          {
            key: `${index}-2`,
            data: {
              name: accountAssessmentSections.interestsSection.displayName,
              status: `${availableInterests}/${totalInterests} ${intlMessages.dimensionsActivatedForSection}`,
            },
            children: [
              {
                key: `${index}-2-0`,
                data: {
                  name: (accountAssessmentSections.interestsSection.dimensions.length > 0
                    ? (
                      <DeactivateInterests
                        sourceAccountId={+rootAccountId}
                        targetAccountId={+account.id}
                        fetchData={fetchData}
                        alreadyInUse={_.intersectionBy(unionInterests, dimensionsInUse, dim => dim.id).length > 0}
                      />
                    )
                    : (
                      <ActivateInterests
                        sourceAccountId={+rootAccountId}
                        targetAccountId={+account.id}
                        fetchData={fetchData}
                      />
                    )),
                  status: null,
                },
              },
              ...unionInterests
                .map((dimension, dimIndex) => {
                  const isActive = _.includes(
                    accountAssessmentSections.interestsSection.dimensions
                      .map(dim => dim.id),
                    dimension.id,
                  );
                  return ({
                    key: `${index}-2-${dimIndex + 1}`,
                    data: {
                      name: <DimensionDisplayName isActive={isActive} dimension={dimension} />,
                      status: null,
                    },
                  });
                }),
            ],
          },
          {
            key: `${index}-3`,
            data: {
              name: 'Survey Competencies',
              status: `${availableTotalAbilities}/${totalAbilities} ${intlMessages.abilitiesActivated}`,
            },
            children: _.sortBy(unionCompetence, comp => comp.displayName)
              .map(
                (competence, compIndex) => {
                  const competenceInAccount = _.find(accountCompetencies, comp => comp.id === competence.id);

                  const availableForThisCompetence = competence.abilities.length;
                  const activatedForThisCompetence = competenceInAccount ? competenceInAccount.abilities.length : 0;
                  return ({
                    key: `${index}-3-${compIndex}`,
                    data: {
                      name: competence.displayName,
                      status: `${activatedForThisCompetence}/${availableForThisCompetence} 
                      ${intlMessages.abilitiesActivatedForCompetence}`,
                    },
                    children: competence.abilities
                      .map((ability, abilityIndex) => {
                        const abilityActivated = competenceInAccount
                          && _.includes(competenceInAccount.abilities.map(ab => ab.id), ability.id);
                        const alreadyInUse = _.includes(abilitiesInUse, ability.name);
                        return ({
                          key: `${index}-3-${compIndex}-${abilityIndex}`,
                          data: {
                            name: <AbilityDisplayName abilityActivated={abilityActivated} ability={ability} />,
                            status: abilityActivated
                              ? (
                                <DeactivateAbility
                                  sourceAccountId={+rootAccountId}
                                  targetAccountId={+account.id}
                                  competence={competence.id}
                                  ability={ability.id}
                                  alreadyInUse={alreadyInUse}
                                  fetchData={fetchData}
                                />
                              ) : (
                                <ActivateAbility
                                  sourceAccountId={+rootAccountId}
                                  targetAccountId={+account.id}
                                  competence={competence.id}
                                  ability={ability.id}
                                  fetchData={fetchData}
                                />
                              ),
                          },
                        });
                      }),
                  });
                },
              ),
          },
        ],
      });
    });
};

export default generateTreeNodes;
